import { storage } from './storage';

const ID_KEY = 'wirespan:id';

/**
 * The stored shape's version.
 *
 * **Do not bump this to add a field.** A bump re-mints every `deviceId`, which
 * destroys every cohort the retention numbers are built from and makes the
 * whole population look new on the same morning. New fields are added as
 * optional and read as absent (GDD Pillar 4); a bump is for a change that
 * genuinely cannot be read forward.
 */
const VERSION = 1;

/** What the browser knows about itself. Only some of it is ever sent. */
export interface Identity {
  v: number;
  /**
   * A random id, and the only identifier this game ever mints. Not derived
   * from the person, the hardware or the network, and not a fingerprint.
   */
  deviceId: string;
  /** The grid number this browser first played. */
  firstGrid: number;
  /** How many separate days this browser has opened the game. */
  visitDays: number;
  /**
   * The last grid number counted toward {@link Identity.visitDays}. Stored so
   * a second launch on the same day is not a second visit, and never sent: a
   * precise last-seen is materially more identifying than a count of days.
   */
  lastGrid: number;
  /** The host of the site this browser first arrived from, if any. */
  referrerHost?: string;
  /**
   * A short source tag the arrival link carried, if any. First-touch only: a
   * later arrival through a different tagged link never overwrites what got
   * this browser here.
   */
  campaign?: string;
}

function mintId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  // A browser too old for randomUUID still gets a random id rather than a
  // derived one; there is no version of this that reads the device.
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(bytes);
  else for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  return [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * A source tag from a link, or `undefined`.
 *
 * `?r=` wins over `?utm_source=` when both are present: a code on a link the
 * game posted itself is a deliberate statement about that link, and a
 * `utm_source` riding along beside it is not. Lowercased before the regex
 * check, so `?utm_source=HN` and `?utm_source=hn` are one campaign rather than
 * two, and a capitalised tag is not silently refused for its case alone.
 * Refused rather than truncated: a tag that does not fit is indistinguishable
 * from no tag at all, and a truncated one is a lie.
 */
const CAMPAIGN_RE = /^[a-z0-9_-]{1,16}$/;
export function campaignCode(search: string): string | undefined {
  try {
    const q = new URLSearchParams(search);
    const raw = (q.get('r') ?? q.get('utm_source') ?? '').trim().toLowerCase();
    return raw && CAMPAIGN_RE.test(raw) ? raw : undefined;
  } catch {
    return undefined;
  }
}

/**
 * The host the player arrived from, or `undefined`.
 *
 * The host only, never the full address: a path can carry a query string, and
 * the promise on the privacy page is that no address is stored.
 */
export function referrerHost(ref: string, here: string): string | undefined {
  if (!ref) return undefined;
  try {
    const from = new URL(ref).host;
    return from && from !== new URL(here).host ? from : undefined;
  } catch {
    return undefined;
  }
}

function parse(raw: string | null): Identity | null {
  if (!raw) return null;
  try {
    const p: unknown = JSON.parse(raw);
    if (!p || typeof p !== 'object') return null;
    const id = p as Partial<Identity>;
    if (id.v !== VERSION || typeof id.deviceId !== 'string' || !id.deviceId) return null;
    return {
      v: VERSION,
      deviceId: id.deviceId,
      firstGrid: typeof id.firstGrid === 'number' ? id.firstGrid : 0,
      visitDays: typeof id.visitDays === 'number' ? id.visitDays : 1,
      lastGrid: typeof id.lastGrid === 'number' ? id.lastGrid : 0,
      ...(typeof id.referrerHost === 'string' ? { referrerHost: id.referrerHost } : {}),
      ...(typeof id.campaign === 'string' ? { campaign: id.campaign } : {}),
    };
  } catch {
    return null;
  }
}

/**
 * Load this browser's id, minting one on a first visit and counting the visit.
 *
 * Called once, before the envelope is snapshotted, so a first-ever session is
 * already `visitDays: 1` rather than zero.
 */
export async function loadIdentity(
  today: number,
  arrival: { referrer?: string; here?: string; search?: string } = {},
): Promise<Identity> {
  const stored = parse(await storage.get(ID_KEY));
  const from = referrerHost(arrival.referrer ?? '', arrival.here ?? 'https://wirespan.app/');
  const tag = campaignCode(arrival.search ?? '');

  const id: Identity = stored ?? {
    v: VERSION,
    deviceId: mintId(),
    firstGrid: today,
    visitDays: 0,
    lastGrid: 0,
    // First touch only. A later arrival through a link says nothing new about
    // where this player came from, and overwriting it would erase what does.
    ...(from ? { referrerHost: from } : {}),
    ...(tag ? { campaign: tag } : {}),
  };

  if (id.lastGrid !== today) {
    id.visitDays += 1;
    id.lastGrid = today;
  }
  await storage.set(ID_KEY, JSON.stringify(id));
  return id;
}

/** The identifying part of the envelope: what actually goes on the wire. */
export function envelopeOf(id: Identity): Record<string, string | number> {
  return {
    deviceId: id.deviceId,
    firstGrid: id.firstGrid,
    visitDays: id.visitDays,
    ...(id.referrerHost ? { referrerHost: id.referrerHost } : {}),
    ...(id.campaign ? { campaign: id.campaign } : {}),
  };
}
